import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageCircle, X, Send, Minimize2, LoaderCircle } from 'lucide-react'
import { env } from '@/config/env'
import { useAuthStore } from '@/stores/authStore'
import { CHAT_MESSAGE_LIMIT } from '@/services/chatbotService'
import { useChatConversation, type ChatMessage } from '@/hooks/useChatConversation'
import { ChatMessageBubble } from './ChatMessageBubble'
import { ChatViewport } from './ChatViewport'
import { TENANT_LANDING } from '@/config/tenantLanding'

// Remount conversation state when the tenant or signed-in identity changes.
export function ChatBot() {
  'use no memo' // Re-read runtime tenant configuration when the application renders.
  const identity = useAuthStore(state => state.isAuthenticated
    ? JSON.stringify([state.user?.sid, state.user?.id, state.user?.email])
    : 'anonymous')
  return <ChatConversation key={JSON.stringify([env('VITE_TENANT_ID'), identity])} />
}

function ChatConversation() {
  const { t } = useTranslation('dashboard')
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const {
    messages, pendingMessage, inputValue, setInputValue,
    error, send, reset: resetConversation, isLoading, canSend,
  } = useChatConversation()
  const [greetingTime, setGreetingTime] = useState(() => new Date())
  const greeting: ChatMessage = {
    id: 'greeting', role: 'assistant', content: t('chatBotGreeting'), timestamp: greetingTime,
  }
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const quickReplies = ['Session', 'Transcript', 'Video', 'OnDemand', 'History'].map(id => ({
    id, text: t(`chatBotStarter${id}`),
  }))

  useEffect(() => {
    // scrollIntoView also scrolls ancestors, including the panel and page.
    // Only the message list should move when an answer or loading state arrives.
    const list = messagesRef.current
    if (!list) return
    const latest = messages[messages.length - 1]
    const selection = !isLoading && !error && latest?.response?.sessionOptions?.length
      ? list.querySelector<HTMLElement>(`[data-message-id="${latest.id}"]`) : null
    list.scrollTop = selection
      ? list.scrollTop + selection.getBoundingClientRect().top - list.getBoundingClientRect().top
      : list.scrollHeight
  }, [messages, isLoading, error, isOpen, isMinimized])

  useEffect(() => {
    if (isOpen && !isMinimized) inputRef.current?.focus({ preventScroll: true })
  }, [isOpen, isMinimized])

  const reset = () => {
    resetConversation()
    setGreetingTime(new Date())
    inputRef.current?.focus({ preventScroll: true })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void send(inputValue)
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-3 sm:bottom-6 sm:left-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-full w-14 h-14 sm:w-16 sm:h-16 shadow-lg transition-all duration-200 hover:scale-110 z-50 group flex items-center justify-center"
        aria-label={t('chatBotOpenChat')}
      >
        <MessageCircle className="w-8 h-8" />
        {/* Tooltip */}
        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          {t('chatBotNeedHelpTooltip')}
          <svg className="absolute top-full right-4" width="8" height="4" viewBox="0 0 8 4" aria-hidden="true"><polygon points="0,0 8,0 4,4" fill="#111827"/></svg>
        </div>
      </button>
    )
  }

  return (
    <ChatViewport>
      <div
        role="region"
        aria-label={t('chatBotSupportTitle')}
        className={`pointer-events-auto grid grid-cols-[minmax(0,1fr)] w-full max-w-full max-h-full overflow-clip bg-white rounded-2xl shadow-2xl ${
          isMinimized ? 'h-auto sm:w-80 grid-rows-[auto]' : 'h-full sm:w-96 sm:h-[560px] grid-rows-[auto_auto_minmax(0,1fr)_auto]'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="hidden sm:block shrink-0 bg-white rounded-full p-2">
              <MessageCircle className="w-5 h-5 text-primary-500" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-sm sm:text-base leading-snug">{t('chatBotSupportTitle')}</h3>
              {!isMinimized && <p className="text-xs text-primary-100 mt-0.5 [@media(max-height:450px)]:hidden">{t('chatBotSubtitle')}</p>}
            </div>
          </div>
          <div className="flex shrink-0 items-center">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="hover:bg-white/20 w-11 h-11 flex items-center justify-center rounded-lg transition-colors"
              aria-label={isMinimized ? t('chatBotMaximize') : t('chatBotMinimize')}
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 w-11 h-11 flex items-center justify-center rounded-lg transition-colors"
              aria-label={t('chatBotCloseChat')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Chat Content */}
        {!isMinimized && (
          <>
            <button type="button" onClick={reset} className="min-h-11 text-xs text-primary-700 hover:underline px-3 sm:px-4 py-2 text-left">
              {t('chatBotNewConversation')}
            </button>
            {/* Messages */}
            <div ref={messagesRef} role="log" aria-label={t('chatBotConversation')} aria-live="polite" className="min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 bg-gradient-to-b from-gray-50 to-white">
              <ChatMessageBubble message={greeting} onNavigate={() => setIsOpen(false)} />
              {messages.map((message) => (
                <ChatMessageBubble key={message.id} message={message} onNavigate={() => setIsOpen(false)}
                  onSelectSession={(session, label) => void send(`${session.title} · ${label} · ${t('chatBotSessionId', { id: session.sessionId })}`, session.sessionId)}
                  onShowMore={page => void send(t('chatBotShowMore'), undefined, page)}
                  selectionDisabled={isLoading} />
              ))}

              {pendingMessage && <ChatMessageBubble message={pendingMessage} onNavigate={() => setIsOpen(false)} />}

              {/* Typing Indicator */}
              {isLoading && (
                <div role="status" className="flex justify-start mb-4">
                  <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-md border border-gray-200">
                    <p className="text-sm text-gray-600 mb-2">{t('chatBotLoading')}</p>
                    <div aria-hidden="true" className="flex gap-1.5">
                      <div className="w-2 h-2 bg-primary-500 rounded-full typing-dot"></div>
                      <div
                        className="w-2 h-2 bg-primary-500 rounded-full typing-dot"
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-primary-500 rounded-full typing-dot"
                        style={{ animationDelay: '0.4s' }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Replies */}
              {messages.length === 0 && !isLoading && (
                <div className="mt-4">
                  <p className="text-xs text-gray-600 mb-3 font-semibold">{t('chatBotQuickQuestions')}</p>
                  <div className="flex flex-wrap gap-2">
                    {quickReplies.map((reply) => (
                      <button
                        key={reply.id}
                        disabled={isLoading}
                        onClick={() => {
                          if (reply.id === 'Session') { setInputValue(reply.text); inputRef.current?.focus({ preventScroll: true }) }
                          else void send(reply.text)
                        }}
                        className="max-w-full min-h-11 text-left [overflow-wrap:anywhere] bg-white border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 text-gray-700 hover:text-primary-700 text-xs font-medium px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md"
                      >
                        {reply.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && <div role="alert" className="py-2 text-sm text-red-700">
                <p>{t(error)}</p>
                <button type="button" disabled={!canSend}
                  onClick={() => void send(inputValue)} className="underline font-medium">
                  {t('chatBotRetry')}
                </button>
              </div>}
            </div>

            {/* Input */}
            <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  aria-label={t('chatBotInputPlaceholder')}
                  maxLength={CHAT_MESSAGE_LIMIT}
                  readOnly={isLoading}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={t(isLoading ? 'chatBotWaitingForReply' : 'chatBotInputPlaceholder')}
                  className="flex-1 min-w-0 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base bg-white text-gray-900 transition-all"
                />
                <button
                  type="submit"
                  disabled={!canSend}
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shrink-0 text-white rounded-xl px-4 py-3 transition-all shadow-md hover:shadow-lg"
                  aria-label={t('chatBotSendMessage')}
                >
                  {isLoading
                    ? <LoaderCircle aria-hidden="true" className="w-5 h-5 animate-spin motion-reduce:animate-none" />
                    : <Send aria-hidden="true" className="w-5 h-5" />}
                </button>
              </form>
              {TENANT_LANDING.footer.supportEmail && (
                <p className="text-xs font-bold text-black mt-2 text-center [overflow-wrap:anywhere] [@media(max-height:450px)]:hidden">
                  {t('chatBotOrEmailUsAt')}{' '}
                  <a href={`mailto:${TENANT_LANDING.footer.supportEmail}`} className="text-sm font-bold text-primary-600 hover:text-primary-700 hover:underline">
                    {TENANT_LANDING.footer.supportEmail}
                  </a>
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </ChatViewport>
  )
}
